import { useState, useCallback, useRef, useMemo } from 'react';

export type ValidationRuleType = 
  | 'name'
  | 'certifications'
  | 'roleCompany'
  | 'careerAspiration'
  | 'location'
  | 'performanceText'
  | 'ttecCommunities'
  | 'performanceSummary'
  | 'timeInRole'
  | 'education'
  | 'description'
  | 'generalText'
  | 'year';

const VALIDATION_RULES: Record<ValidationRuleType, {
  blockedPattern?: RegExp;
  blockedPatterns?: RegExp[];
  maxLength: number;
}> = {
  name: {
    blockedPattern: /[0-9@#$%^&*()_+={}[\]|\\:;"<>?/~`!]/g,
    maxLength: 100
  },
  certifications: {
    blockedPattern: /[@#$%^*_+={}[\]|\\:;"'<>?~`!]/g,
    maxLength: 500
  },
  roleCompany: {
    blockedPattern: /[@#$%^*_+={}[\]|\\:;"'<>?~`!()]/g,
    maxLength: 100
  },
  careerAspiration: {
    blockedPattern: /[@#$%^&*_+={}[\]|\\:;"'<>?~`!.,()]/g,
    maxLength: 150
  },
  location: {
    blockedPattern: /[@#$%^&*_+={}[\]|\\:;"'<>?~`!./()]/g,
    maxLength: 200
  },
  // TTEC Communities - Only allows letters, numbers, spaces, comma, hyphen, period
  ttecCommunities: {
    blockedPattern: /[^A-Za-z0-9\s,.\-]/g,
    blockedPatterns: [
      /<script/gi,
      /javascript:/gi,
      /onerror\s*=/gi,
      /onload\s*=/gi,
      /onclick\s*=/gi,
      /onmouseover\s*=/gi,
      /<iframe/gi,
      /<object/gi,
      /<img/gi,
      /<svg/gi,
      /<embed/gi,
    ],
    maxLength: 200
  },
  // Performance Summary - Allows letters, numbers, spaces, period, comma, !, ?, :, ;, -, ', (), newlines
  performanceSummary: {
    blockedPattern: /[^A-Za-z0-9\s.,!?:;\-'()\n\r]/g,
    blockedPatterns: [
      /<script/gi,
      /javascript:/gi,
      /onerror\s*=/gi,
      /onload\s*=/gi,
      /onclick\s*=/gi,
      /onmouseover\s*=/gi,
      /<iframe/gi,
      /<object/gi,
      /<img/gi,
      /<svg/gi,
      /<embed/gi,
      /['"];\s*--/g,
      /\/\*.*\*\//g,
    ],
    maxLength: 2000
  },
  performanceText: {
    blockedPatterns: [
      /['"];\s*--/g,
      /\/\*.*\*\//g,
      /<script/gi,
      /javascript:/gi,
      /onerror\s*=/gi,
      /onload\s*=/gi,
      /onclick\s*=/gi,
      /<iframe/gi,
      /<object/gi,
    ],
    maxLength: 2000
  },
  timeInRole: {
    blockedPattern: /[@#$%^&*()_+={}[\]|\\:;"'<>?/~`!,]/g,
    maxLength: 50
  },
  education: {
    blockedPattern: /[@#$%^&*_+={}[\]|\\:;"<>?/~`!]/g,
    maxLength: 100
  },
  description: {
    blockedPattern: /[@#$%^*_+={}[\]|\\<>~`]/g,
    maxLength: 500
  },
  generalText: {
    blockedPattern: /[@#$%^*_+={}[\]|\\:;"<>?~`!]/g,
    maxLength: 500
  },
  year: {
    blockedPattern: /[^0-9]/g,
    maxLength: 4
  }
};

export const FIELD_CONFIG: Record<string, ValidationRuleType> = {
  time_in_current_role: 'timeInRole',
  certificates: 'certifications',
  education_degree: 'education',
  current_location: 'location',
  location_others_comments: 'location',
  next_role_preference: 'careerAspiration',
  future_career_aspirations: 'careerAspiration',
  roleTitle: 'roleCompany',
  companyName: 'roleCompany',
  roleDescription: 'description',
  ttec_communities: 'ttecCommunities',
  ttecCommunity: 'ttecCommunities',
  accomplishments: 'performanceSummary',
  redo: 'performanceSummary',
  redoBusiness: 'performanceSummary',
  goals: 'performanceSummary',
  last_year_personal_development_review: 'performanceSummary',
  goal_next_two_quarters: 'performanceSummary',
  last_year_top_achievement: 'performanceSummary',
  redo_for_previous_business_year: 'performanceSummary',
  improvements: 'performanceSummary',
};

export const VALIDATION_ERROR_MESSAGE = 'Special characters are not allowed';

interface ValidationState {
  errors: Record<string, boolean>;
  blockedCharsMap: Record<string, string[]>;
  firstErrorField: string | null;
}

interface UseFormValidationReturn {
  errors: Record<string, boolean>;
  blockedCharsMap: Record<string, string[]>;
  firstErrorField: string | null;
  validateField: (fieldName: string, value: string, customRule?: ValidationRuleType) => boolean;
  validateAndSanitize: (fieldName: string, value: string, customRule?: ValidationRuleType) => string;
  clearError: (fieldName: string) => void;
  clearAllErrors: () => void;
  hasErrors: boolean;
  registerRef: (fieldName: string) => (el: HTMLElement | null) => void;
  scrollToFirstError: () => void;
  scrollToField: (fieldName: string) => void;
  getFieldError: (fieldName: string) => boolean;
  getInputClassName: (fieldName: string, baseClass?: string) => string;
  setFieldBlockedChars: (fieldName: string, chars: string[]) => void;
}

export function useFormValidation(): UseFormValidationReturn {
  const [state, setState] = useState<ValidationState>({
    errors: {},
    blockedCharsMap: {},
    firstErrorField: null
  });
  
  const fieldRefs = useRef<Record<string, HTMLElement | null>>({});

  const validateField = useCallback((
    fieldName: string,
    value: string,
    customRule?: ValidationRuleType
  ): boolean => {
    if (!value || value.trim() === '') {
      setState(prev => ({
        ...prev,
        errors: { ...prev.errors, [fieldName]: false }
      }));
      return true;
    }

    const ruleType = customRule || FIELD_CONFIG[fieldName] || 'generalText';
    const rule = VALIDATION_RULES[ruleType];
    
    let hasError = false;
    let foundBlockedChars: string[] = [];

    if (rule.blockedPatterns) {
      for (const pattern of rule.blockedPatterns) {
        if (pattern.test(value)) {
          hasError = true;
          foundBlockedChars = ['unsafe content'];
          break;
        }
      }
    }
    
    if (!hasError && rule.blockedPattern) {
      const matches = value.match(rule.blockedPattern);
      if (matches) {
        hasError = true;
        foundBlockedChars = Array.from(new Set(matches));
      }
    }

    setState(prev => {
      const newErrors = { ...prev.errors, [fieldName]: hasError };
      const newBlockedCharsMap = { ...prev.blockedCharsMap };
      if (hasError && foundBlockedChars.length > 0) {
        newBlockedCharsMap[fieldName] = foundBlockedChars;
      } else {
        delete newBlockedCharsMap[fieldName];
      }
      const errorFields = Object.entries(newErrors)
        .filter(([_, hasErr]) => hasErr)
        .map(([field]) => field);
      
      return {
        errors: newErrors,
        blockedCharsMap: newBlockedCharsMap,
        firstErrorField: errorFields.length > 0 ? errorFields[0] : null
      };
    });

    return !hasError;
  }, []);

  const validateAndSanitize = useCallback((
    fieldName: string,
    value: string,
    customRule?: ValidationRuleType
  ): string => {
    if (!value) return '';

    const ruleType = customRule || FIELD_CONFIG[fieldName] || 'generalText';
    const rule = VALIDATION_RULES[ruleType];
    
    let sanitized = value;
    let wasModified = false;
    let foundBlockedChars: string[] = [];

    if (rule.blockedPatterns) {
      for (const pattern of rule.blockedPatterns) {
        const newValue = sanitized.replace(pattern, '');
        if (newValue !== sanitized) {
          wasModified = true;
          foundBlockedChars.push('unsafe content');
          sanitized = newValue;
        }
      }
    }
    
    if (rule.blockedPattern) {
      const matches = sanitized.match(rule.blockedPattern);
      if (matches) {
        foundBlockedChars = [...foundBlockedChars, ...Array.from(new Set(matches))];
      }
      const newValue = sanitized.replace(rule.blockedPattern, '');
      if (newValue !== sanitized) {
        wasModified = true;
        sanitized = newValue;
      }
    }

    if (sanitized.length > rule.maxLength) {
      sanitized = sanitized.slice(0, rule.maxLength);
    }

    setState(prev => {
      const newErrors = { ...prev.errors, [fieldName]: wasModified };
      const newBlockedCharsMap = { ...prev.blockedCharsMap };
      if (wasModified && foundBlockedChars.length > 0) {
        newBlockedCharsMap[fieldName] = foundBlockedChars;
      } else {
        delete newBlockedCharsMap[fieldName];
      }
      const errorFields = Object.entries(newErrors)
        .filter(([_, hasErr]) => hasErr)
        .map(([field]) => field);
      
      return {
        errors: newErrors,
        blockedCharsMap: newBlockedCharsMap,
        firstErrorField: errorFields.length > 0 ? errorFields[0] : null
      };
    });

    return sanitized;
  }, []);

  const clearError = useCallback((fieldName: string) => {
    setState(prev => {
      const newErrors = { ...prev.errors };
      delete newErrors[fieldName];
      const newBlockedCharsMap = { ...prev.blockedCharsMap };
      delete newBlockedCharsMap[fieldName];
      
      const errorFields = Object.entries(newErrors)
        .filter(([_, hasErr]) => hasErr)
        .map(([field]) => field);
      
      return {
        errors: newErrors,
        blockedCharsMap: newBlockedCharsMap,
        firstErrorField: errorFields.length > 0 ? errorFields[0] : null
      };
    });
  }, []);

  const clearAllErrors = useCallback(() => {
    setState({ errors: {}, blockedCharsMap: {}, firstErrorField: null });
  }, []);

  const registerRef = useCallback((fieldName: string) => {
    return (el: HTMLElement | null) => {
      fieldRefs.current[fieldName] = el;
    };
  }, []);
  const FIELD_NAME_ALIASES: Record<string, string[]> = useMemo(() => ({
    willingness_to_relocate: ['willingnessToRelocate', 'willingness_to_relocate'],
    accomplishments: ['last_year_top_achievement', 'accomplishments'],
    last_year_top_achievement: ['last_year_top_achievement', 'accomplishments'],
    redo: ['last_year_personal_development_review', 'redo'],
    last_year_personal_development_review: ['last_year_personal_development_review', 'redo'],
    redoBusiness: ['redo_for_previous_business_year', 'redoBusiness'],
    redo_for_previous_business_year: ['redo_for_previous_business_year', 'redoBusiness'],
    goals: ['goal_next_two_quarters', 'goals'],
    goal_next_two_quarters: ['goal_next_two_quarters', 'goals'],
  }), []);

  const findFieldElement = useCallback((fieldName: string): HTMLElement | null => {
    if (fieldRefs.current[fieldName]) {
      return fieldRefs.current[fieldName];
    }
    const el = document.querySelector<HTMLElement>(
      `[name="${fieldName}"], textarea[name="${fieldName}"]`
    );
    if (el) return el;

    const aliases = FIELD_NAME_ALIASES[fieldName];
    if (aliases) {
      for (const alias of aliases) {
        if (fieldRefs.current[alias]) return fieldRefs.current[alias];
        const aliasEl = document.querySelector<HTMLElement>(
          `[name="${alias}"], textarea[name="${alias}"]`
        );
        if (aliasEl) return aliasEl;
      }
    }
    return null;
  }, [FIELD_NAME_ALIASES]);

  const scrollToFirstError = useCallback(() => {
  const fieldName = state.firstErrorField
      || Object.keys(state.blockedCharsMap)[0]
      || Object.keys(state.errors).find(k => state.errors[k]);
    if (fieldName) {
      const el = findFieldElement(fieldName);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        el.focus();
      }
    }
  }, [state.firstErrorField, state.blockedCharsMap, state.errors, findFieldElement]);

  const scrollToField = useCallback((fieldName: string) => {
    const el = findFieldElement(fieldName);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      el.focus();
    }
  }, [findFieldElement]);

  const getFieldError = useCallback((fieldName: string): boolean => {
    return state.errors[fieldName] || false;
  }, [state.errors]);

  const getInputClassName = useCallback((fieldName: string, baseClass = ''): string => {
    const hasError = state.errors[fieldName];
    return `${baseClass}${hasError ? ' is-invalid validation-error' : ''}`.trim();
  }, [state.errors]);

  const hasErrors = useMemo(() => {
    return Object.values(state.errors).some(hasError => hasError);
  }, [state.errors]);

  const setFieldBlockedChars = useCallback((fieldName: string, chars: string[]) => {
    setState(prev => {
      const newBlockedCharsMap = { ...prev.blockedCharsMap };
      if (chars.length > 0) {
        newBlockedCharsMap[fieldName] = chars;
        return {
          ...prev,
          errors: { ...prev.errors, [fieldName]: true },
          blockedCharsMap: newBlockedCharsMap,
          firstErrorField: prev.firstErrorField || fieldName
        };
      } else {
        delete newBlockedCharsMap[fieldName];
        const newErrors = { ...prev.errors };
        delete newErrors[fieldName];
        const errorFields = Object.entries(newErrors)
          .filter(([_, hasErr]) => hasErr)
          .map(([field]) => field);
        return {
          errors: newErrors,
          blockedCharsMap: newBlockedCharsMap,
          firstErrorField: errorFields.length > 0 ? errorFields[0] : null
        };
      }
    });
  }, []);

  return {
    errors: state.errors,
    blockedCharsMap: state.blockedCharsMap,
    firstErrorField: state.firstErrorField,
    validateField,
    validateAndSanitize,
    clearError,
    clearAllErrors,
    hasErrors,
    registerRef,
    scrollToFirstError,
    scrollToField,
    getFieldError,
    getInputClassName,
    setFieldBlockedChars
  };
}

export default useFormValidation;
